import { CheckoutSpinner } from "@iewc/Components/CheckoutSpinner"; // VERNDALE CUSTOMIZATION - import ValidateCartApiParameter
import { ValidateCartApiParameter } from "@iewc/Services/CustomCartValidationService"; // VERNDALE CUSTOMIZATION - import customValidateCart handler
import customValidateCart from "@iewc/Store/Handlers/CheckoutShipping/ValidateCart"; // VERNDALE CUSTOMIZATION - import custom CheckoutSpinner component
import { scrollToFirstFormError } from "@insite/client-framework/Common/CustomerSelectors";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import clearMessages from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/ClearMessages";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
    clearMessages,
    customValidateCart: makeHandlerChainAwaitable(customValidateCart), // VERNDALE CUSTOMIZATION - add customValidateCart handler
    loadCurrentCart,
};

const mapStateToProps = (state: ApplicationState) => {
    const cartId = state.pages.checkoutShipping.cartId;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        cartId: state.pages.checkoutShipping.cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        location: getLocation(state),
        isUpdatingCustomer: state.pages.checkoutShipping.isUpdatingCustomer,
        cartState,
        shipToState: getShipToState(state, cartState.value?.shipToId),
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
    };
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
    isValidatingCart: boolean;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
    isValidatingCart: false,
});

class CheckoutShippingPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
            isValidatingCart: false,
        };
    }

    async componentDidMount() {
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId });
            this.props.setInitialValues();
        }
        this.props.setIsPreloadingData({ isPreloadingData: false });
        this.props.clearMessages();
    }
    // VERNDALE CUSTOMIZATION START
    // Here we build the cart to validate, and pass it to the customValidateCart handler
    buildCart = (): ValidateCartApiParameter => {
        const cart = this.props.cartState.value;
        const shipTo = this.props.shipToState.value;

        if (!cart || !shipTo) {
            return {} as ValidateCartApiParameter;
        }

        const cartToValidate: ValidateCartApiParameter = {
            lines: [],
            orderNumber: cart.orderNumber || "",
            shipToCustNbr: shipTo.customerSequence || shipTo.customerNumber || "", // fallback for the selectedShipTo
            shipToRegion: shipTo.state?.abbreviation || "",
        };

        cart.cartLines?.forEach(line => {
            cartToValidate.lines.push({
                ProductNbr: line.erpNumber || "",
                CartQuantity: line.qtyOrdered?.toString() || "",
                QtyUom: line.unitOfMeasure || "",
                LooseEnds: line.properties?.looseEnds || "No",
                CartlineId: line.id || "",
                IsQuote: line.properties?.isQuote || false,
            });
        });

        return cartToValidate;
    };
    // VERNDALE CUSTOMIZATION END

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            // VERNDALE CUSTOMIZATION START - call customValidateCart handler and redirect to cart page if there are errors
            const cartToValidate = this.buildCart();
            this.setState({ isValidatingCart: true });
            await this.props.customValidateCart({
                apiParameter: cartToValidate,
                onComplete: async resultProps => {
                    this.setState({ isValidatingCart: false });
                    if (resultProps.apiResult.results && resultProps.apiResult.results.length > 0) {
                        this.props.history.push(this.props.cartPageLink!.url);
                    } else {
                        this.setState({ isRedirecting: true });
                        // TODO ISC-12556
                        await this.props.updateCart({});
                        const continueUrl = this.props.cartId
                            ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
                            : this.props.reviewAndSubmitPageLink!.url;
                        this.props.history.push(continueUrl);
                    }
                },
            });
        } else {
            scrollToFirstFormError();
        }
    };

    // VERNDALE CUSTOMIZATION END

    render() {
        return (
            <Page data-test-selector="checkoutShipping">
                <LoadingOverlay loading={this.props.isUpdatingCustomer}>
                    <CheckoutShippingFormContext.Provider value={this.state}>
                        <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                            <Zone zoneName="Content" contentId={this.props.id} />
                            {/* VERNDALE CUSTOMIZATION */}
                            {this.state.isValidatingCart && <CheckoutSpinner></CheckoutSpinner>}
                            {/* VERNDALE CUSTOMIZATION */}
                        </form>
                    </CheckoutShippingFormContext.Provider>
                </LoadingOverlay>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutShippingPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "CheckoutShippingPage" instead of this constant.
 */
export const CheckoutShippingPageContext = "CheckoutShippingPage";
